import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isTokenValid } from '@/utils/tokenValidation';
import {
  deleteAllNotifications,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/services/notifications.service';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'application' | 'system' | 'info';
  timestamp: Date;
  read: boolean;
  titleKey?: string;
  messageKey?: string;
  vacanteId?: string;
  vacanteTitle?: string;
  applicationId?: string;
  candidateName?: string;
  candidateEmail?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/** @deprecated Las notificaciones ya no usan localStorage; claves conservadas por compatibilidad de imports. */
export const STORAGE_KEY_RECRUITER = 'air_notifications';
/** @deprecated */
export const STORAGE_KEY_ADMIN = 'air_notifications_admin';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof localStorage === 'undefined' || !isTokenValid(localStorage.getItem('user_token'))) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    try {
      const [items, count] = await Promise.all([fetchNotifications(), fetchUnreadCount()]);
      setNotifications(items);
      setUnreadCount(count);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const markAsRead = useCallback(
    (id: string) => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      void markNotificationRead(id).then(() => void refresh());
    },
    [refresh],
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    void markAllNotificationsRead().then(() => void refresh());
  }, [refresh]);

  const clearNotifications = useCallback(() => {
    void (async () => {
      const ok = await deleteAllNotifications();
      if (ok) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        await refresh();
      }
    })();
  }, [refresh]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        refresh,
        markAsRead,
        markAllAsRead,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
